// 付箋（sticky）の本文を、付箋サイズへちょうど収まる文字サイズへ自動調整するためのユーティリティ。
//
// 方針:
// - 付箋の文字サイズは保存値（永続フィールド）を持たず、本文 + 付箋サイズから決定的に算出する派生値とする。
//   これにより、リサイズや他参加者の本文更新でも常に最適サイズへ追従でき、サーバ / op を増やさずに済む。
// - 付箋の余白がなるべく無くなるよう、枠内に収まる範囲で最大の文字サイズを二分探索で求める。
// - 左下の最終更新者名と本文がぶつからないよう、本文の利用可能高さから署名領域分を差し引く。

/** 付箋の内側パディング（sticky-object.tsx の outer コンテナ padding と一致させる）。 */
export const STICKY_PADDING_PX = 12;

/**
 * 左下の最終更新者名表示のために、本文側で確保しておく下部の高さ。
 * sticky-object.tsx のエディタ paddingBottom と本値を共有することで、自動調整後の本文が
 * 署名表示へ干渉しない（重ならない）ことを保証する。
 */
export const STICKY_SIGNATURE_RESERVE_PX = 18;

/** 自動調整で許容する文字サイズの下限・上限（px）。極端な値で表示が壊れないよう範囲を設ける。 */
export const STICKY_FONT_MIN_PX = 8;
export const STICKY_FONT_MAX_PX = 80;

/** 本文が空のときに使う既定サイズ（編集開始直後の入力サイズにもなる）。 */
export const STICKY_FONT_DEFAULT_PX = 16;

/**
 * 付箋本文の行高比（line-height ÷ font-size）。
 * アプリ全体の既定行高（styles.css の body { line-height: 1.5 }）に合わせる。
 * DOM 計測では実際の computed line-height を優先し、本値は computed が "normal" 等で取得できない場合の
 * フォールバックとして使う。PDF 出力（Canvas 2D）では DOM の computed を参照できないため本値を用い、
 * 画面表示と出力の見た目（収まる文字サイズ）を揃える。
 */
export const STICKY_LINE_HEIGHT_RATIO = 1.5;

/**
 * 枠内に収まる範囲で最大の文字サイズ（整数 px）を二分探索で求める純粋関数。
 *
 * `fits(px)` は「その文字サイズで本文が枠内に収まるか」を返す単調関数を想定する
 * （px が大きいほど収まりにくい）。どのサイズでも収まらない場合は `minPx` を返す。
 * DOM / Canvas いずれの計測方法でも共有できるよう、計測手段は呼び出し側から注入する。
 */
export function computeBestFitFontSize(options: {
  readonly minPx: number;
  readonly maxPx: number;
  readonly fits: (fontPx: number) => boolean;
}): number {
  const { minPx, maxPx, fits } = options;
  if (maxPx < minPx) return minPx;
  let lo = minPx;
  let hi = maxPx;
  let best = minPx;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/** 付箋サイズから本文の利用可能領域（px）を求める。負値は 0 に丸める。 */
export function computeStickyTextArea(params: {
  readonly boxWidth: number;
  readonly boxHeight: number;
  readonly hasSignature: boolean;
}): { readonly width: number; readonly height: number } {
  const width = Math.max(0, params.boxWidth - STICKY_PADDING_PX * 2);
  const reserve = params.hasSignature ? STICKY_SIGNATURE_RESERVE_PX : 0;
  const height = Math.max(0, params.boxHeight - STICKY_PADDING_PX * 2 - reserve);
  return { width, height };
}

/** 既定サイズを許容範囲へ丸める。 */
function clampToFontRange(px: number): number {
  return Math.min(STICKY_FONT_MAX_PX, Math.max(STICKY_FONT_MIN_PX, Math.round(px)));
}

// DOM 計測用の隠し要素（モジュール内で 1 つだけ生成して使い回す）。
let measurerEl: HTMLDivElement | null = null;

function getMeasurer(): HTMLDivElement | null {
  if (typeof document === "undefined") return null;
  if (measurerEl && measurerEl.isConnected) return measurerEl;
  const el = document.createElement("div");
  el.setAttribute("aria-hidden", "true");
  // 画面外に隠して配置し、レイアウトへ影響を与えずに高さ・幅だけを計測する。
  el.style.position = "fixed";
  el.style.left = "-99999px";
  el.style.top = "-99999px";
  el.style.visibility = "hidden";
  el.style.pointerEvents = "none";
  el.style.margin = "0";
  el.style.padding = "0";
  el.style.border = "0";
  el.style.boxSizing = "border-box";
  // 表示側（sticky-object.tsx のエディタ）と折り返し条件を一致させる。
  el.style.whiteSpace = "pre-wrap";
  el.style.overflowWrap = "anywhere";
  document.body.appendChild(el);
  measurerEl = el;
  return el;
}

/**
 * DOM 計測により、付箋の本文がちょうど収まる文字サイズ（px）を求める。
 *
 * `sample`（実際のエディタ要素）から font-family / font-weight / font-style / letter-spacing と
 * 行高比（line-height ÷ font-size）を引き継ぎ、表示と計測のズレを抑える。
 * DOM が無い環境（テスト等）では既定サイズを返す。
 */
export function measureStickyFitFontSize(params: {
  readonly text: string;
  readonly boxWidth: number;
  readonly boxHeight: number;
  readonly hasSignature: boolean;
  readonly sample: HTMLElement;
}): number {
  // 空本文（長さ 0）はサイズ制約が無いため既定サイズを返す。
  // 空白・改行のみの本文は「内容あり」として計測し、PDF 出力（whitespace も描画対象）と挙動を揃える。
  if (params.text.length === 0) return clampToFontRange(STICKY_FONT_DEFAULT_PX);
  const measurer = getMeasurer();
  if (!measurer) return clampToFontRange(STICKY_FONT_DEFAULT_PX);
  const area = computeStickyTextArea(params);
  if (area.width <= 0 || area.height <= 0) return STICKY_FONT_MIN_PX;

  const computed = window.getComputedStyle(params.sample);
  measurer.style.fontFamily = computed.fontFamily;
  measurer.style.fontWeight = computed.fontWeight;
  measurer.style.fontStyle = computed.fontStyle;
  measurer.style.letterSpacing = computed.letterSpacing;
  // line-height は px ではなく「現在の font-size に対する比率」で持たせ、候補サイズでも比率が保たれるようにする。
  const baseFontPx = Number.parseFloat(computed.fontSize);
  const baseLineHeightPx = Number.parseFloat(computed.lineHeight);
  const lineHeightRatio =
    Number.isFinite(baseFontPx) && baseFontPx > 0 && Number.isFinite(baseLineHeightPx)
      ? baseLineHeightPx / baseFontPx
      : STICKY_LINE_HEIGHT_RATIO;
  measurer.style.lineHeight = String(lineHeightRatio);
  measurer.style.width = `${area.width}px`;
  measurer.textContent = params.text;

  const fits = (fontPx: number): boolean => {
    measurer.style.fontSize = `${fontPx}px`;
    // 高さが収まり、かつ折り返し後の幅も超過しないことを確認する（長い英単語は overflow-wrap で分割される）。
    return measurer.scrollHeight <= area.height && measurer.scrollWidth <= Math.ceil(area.width);
  };
  return computeBestFitFontSize({
    minPx: STICKY_FONT_MIN_PX,
    maxPx: STICKY_FONT_MAX_PX,
    fits,
  });
}
